/**
 * TanStack Query functions for user-related API calls
 */

import { useQuery } from "@tanstack/vue-query";
import { computed } from "vue";
import { getUserApiUrl } from "../config/urlConfig.js";
import { queryKeys } from "../queryKeys.js";

/**
 * Search users for booking on behalf of others
 * @param {string} searchQuery - Search query string
 * @param {string} searchUrl - Full search URL (for now we use the passed URL)
 * @returns {Promise<Array>} Array of user search results
 */
export const searchUsers = async (searchQuery, searchUrl) => {
	if (!searchUrl) {
		throw new Error("Search URL not available");
	}

	if (!searchQuery || searchQuery.trim().length === 0) {
		return [];
	}

	const url = `${searchUrl}?q=${encodeURIComponent(searchQuery)}`;

	const response = await fetch(url, {
		method: "GET",
		headers: {
			Accept: "application/json",
		},
		credentials: "same-origin",
	});

	if (!response.ok) {
		throw new Error(`Search failed: ${response.status}`);
	}

	const data = await response.json();
	return data || [];
};

/**
 * Alternative user search function using the URL config
 * This assumes the search endpoint is at /search on the user API
 * @param {string} searchQuery - Search query string
 * @returns {Promise<Array>} Array of user search results
 */
export const searchUsersViaConfig = async (searchQuery) => {
	if (!searchQuery || searchQuery.trim().length === 0) {
		return [];
	}

	const baseUrl = getUserApiUrl();
	const url = `${baseUrl}/search?q=${encodeURIComponent(searchQuery)}`;

	const response = await fetch(url, {
		method: "GET",
		headers: {
			Accept: "application/json",
		},
		credentials: "same-origin",
	});

	if (!response.ok) {
		throw new Error(`Search failed: ${response.status}`);
	}

	const data = await response.json();
	return data || [];
};

/**
 * Fetch current user's bookings
 * @param {Object} options - Query options
 * @param {string} [options.from] - Start date for filtering bookings (ISO string)
 * @param {string} [options.until] - End date for filtering bookings (ISO string)
 * @returns {Promise<Object>} Object with bookings array
 */
export const fetchMyBookings = async (options = {}) => {
	const { from, until } = options;
	const baseUrl = getUserApiUrl();
	const url = `${baseUrl}/my-bookings`;

	const requestBody = {};
	if (from) requestBody.from = from;
	if (until) requestBody.until = until;

	const response = await fetch(url, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Accept: "application/json",
		},
		credentials: "same-origin",
		body: JSON.stringify(requestBody),
	});

	if (!response.ok) {
		throw new Error(`Failed to fetch my bookings: ${response.status}`);
	}

	const data = await response.json();
	return data;
};

/**
 * TanStack Query configuration for user search
 * @param {string} searchQuery - Search query string
 * @param {string} [searchUrl] - Optional full search URL (fallback to config)
 * @returns {Object} Query configuration object
 */
export const useUserSearchQuery = (searchQuery, searchUrl) => {
	return {
		queryKey: queryKeys.userSearch(searchQuery),
		queryFn: ({ signal }) => {
			if (signal?.aborted) {
				throw new Error("Query was cancelled");
			}

			// Use provided searchUrl or fallback to config-based approach
			if (searchUrl) {
				return searchUsers(searchQuery, searchUrl);
			} else {
				return searchUsersViaConfig(searchQuery);
			}
		},
		staleTime: 1000 * 60 * 5, // 5 minutes - user data is relatively stable
		gcTime: 1000 * 60 * 15, // 15 minutes
		retry: 2,
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
		// Only enable if search query has minimum length
		enabled: !!searchQuery && searchQuery.trim().length >= 2,
		// Avoid rapid successive calls while typing
		refetchOnWindowFocus: false,
	};
};

/**
 * Fetch current user's point transactions
 * @returns {Promise<Object>} Object with totalPoints, usedPoints, and transactions array
 */
export const fetchMyTransactions = async () => {
	const baseUrl = getUserApiUrl();
	const url = `${baseUrl}/my-transactions`;

	const response = await fetch(url, {
		method: "GET",
		headers: {
			Accept: "application/json",
		},
		credentials: "same-origin",
	});

	if (!response.ok) {
		// For 400 errors, try to parse the error response
		if (response.status === 400) {
			try {
				const errorData = await response.json();
				const error = new Error(
					errorData.error ||
						`Failed to fetch my transactions: ${response.status}`
				);
				error.status = response.status;
				error.response = { status: response.status };
				error.data = errorData;
				throw error;
			} catch (parseError) {
				// If we can't parse the JSON, fallback to generic error
				const error = new Error(
					`Failed to fetch my transactions: ${response.status}`
				);
				error.status = response.status;
				error.response = { status: response.status };
				throw error;
			}
		}

		// For other errors, throw generic error
		const error = new Error(
			`Failed to fetch my transactions: ${response.status}`
		);
		error.status = response.status;
		error.response = { status: response.status };
		throw error;
	}

	const data = await response.json();
	return data;
};

/**
 * TanStack Query hook for fetching current user's bookings
 * @param {Object} options - Query options
 * @param {string} [options.from] - Start date for filtering bookings (ISO string)
 * @param {string} [options.until] - End date for filtering bookings (ISO string)
 * @returns {Object} TanStack Query result
 */
export const useMyBookingsQuery = (options = {}) => {
	const { from, until } = options;
	const dateRange = { from, until };

	return useQuery({
		queryKey: queryKeys.myBookings(dateRange),
		queryFn: ({ signal }) => {
			if (signal?.aborted) {
				throw new Error("Query was cancelled");
			}
			return fetchMyBookings(options);
		},
		staleTime: 1000 * 60 * 2, // 2 minutes - booking data changes frequently
		gcTime: 1000 * 60 * 10, // 10 minutes
		retry: 2,
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
		refetchOnWindowFocus: true, // Refetch when user returns to tab
	});
};

/**
 * TanStack Query hook for fetching current user's point transactions
 *
 * The endpoint answers 400 for everyone but students, so callers that can also be
 * rendered for staff/admins must gate the query with `enabled`.
 *
 * @param {Object} [options] - Query options
 * @param {boolean|Ref<boolean>} [options.enabled] - Whether the query should run
 * @returns {Object} TanStack Query result
 */
export const useMyTransactionsQuery = ({ enabled = true } = {}) => {
	return useQuery({
		queryKey: queryKeys.myTransactions(),
		queryFn: ({ signal }) => {
			if (signal?.aborted) {
				throw new Error("Query was cancelled");
			}
			return fetchMyTransactions();
		},
		staleTime: 1000 * 60 * 2, // 2 minutes - transaction data changes with bookings
		gcTime: 1000 * 60 * 10, // 10 minutes
		retry: 0,
		refetchOnWindowFocus: true, // Refetch when user returns to tab
		enabled,
	});
};

/**
 * Fetch opening hours for a building for today and the next 14 days
 * @param {number} buildingId - Building ID
 * @returns {Promise<Object>} Object with buildingId and openingHours
 */
export const fetchBuildingOpeningHours = async (buildingId) => {
	if (!buildingId) {
		throw new Error("Building ID is required");
	}

	const baseUrl = getUserApiUrl();
	const url = `${baseUrl}/opening-hours`;

	const response = await fetch(url, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Accept: "application/json",
		},
		credentials: "same-origin",
		body: JSON.stringify({ buildingId }),
	});

	if (!response.ok) {
		throw new Error(`Failed to fetch opening hours: ${response.status}`);
	}

	const data = await response.json();
	return data;
};

/**
 * TanStack Query hook for fetching building opening hours
 * @param {number|Ref<number>} buildingId - Building ID (can be a ref or computed)
 * @returns {Object} TanStack Query result
 */
export const useBuildingOpeningHoursQuery = (buildingId) => {
	const buildingIdValue = computed(() => {
		// Handle both direct values and refs/computed values
		return typeof buildingId === 'object' && buildingId.value !== undefined 
			? buildingId.value 
			: buildingId;
	});

	return useQuery({
		queryKey: computed(() => queryKeys.buildingOpeningHours(buildingIdValue.value)),
		queryFn: ({ signal }) => {
			if (signal?.aborted) {
				throw new Error("Query was cancelled");
			}
			return fetchBuildingOpeningHours(buildingIdValue.value);
		},
		staleTime: 1000 * 60 * 30, // 30 minutes - opening hours don't change frequently
		gcTime: 1000 * 60 * 60, // 1 hour
		retry: 2,
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
		enabled: computed(() => !!buildingIdValue.value), // Only run if buildingId is provided
		refetchOnWindowFocus: false, // Opening hours are relatively static
	});
};
